import { ListItem, TabStyles } from '@tableau/widgets';
import { DataGrid } from '@tableau/widgets-datagrid';

import { DataGridCells } from '~commonInfra/components/dataGrid/DataGridCells';
import { SortableHeader } from '~commonInfra/components/dataGrid/SortableHeader';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { CheckboxCell } from '~src/spacesPlatform/components/ListView/dataGridCells/CheckboxCell';
import { CheckboxHeader } from '~src/spacesPlatform/components/ListView/dataGridCells/CheckboxHeader';
import { ListCell } from '~src/spacesPlatform/components/ListView/dataGridCells/ListCell';
import { PlainTextCell, PlainTextCellWithTransform } from '~src/spacesPlatform/components/ListView/dataGridCells/PlainTextAndNumberCells';
import { PlainTextHeader } from '~src/spacesPlatform/components/ListView/dataGridCells/PlainTextHeader';

import { Messages } from '../../../codegen/Localize';
import { FlowOutputStepInfo } from '../../models/FlowOutputStepInfo';
import { RunFlowSubMenuItem, writeOptionsFull, writeOptionsIncremental, writeOptionsRunAllFull, writeOptionsRunAllIncremental } from './RunFlowSubMenuItem';

const styleOverride = {
  paddingLeft: 0,
};
const headerStyle = {
  fontSize: TabStyles.Typography.Sizing.Default,
  paddingRight: 6,
  fontWeight: 500,
};

export interface FlowOutputStepGridProps {
  isHeaderCheckboxChecked: boolean;
  outputsSelected: { readonly [key: string]: boolean };
  outputsIsIncremental: { readonly [key: string]: boolean };
  onCheckboxClicked: (row: DataGrid.Row) => void;
  onHeaderCheckboxClicked: () => void;
  onIsIncrementalChange: (row: DataGrid.Row, isIncremental: boolean) => void;
  areFutureOutputsIncluded: boolean;
}

export module FlowOutputStepGrid {
  export function columns(props: FlowOutputStepGridProps): DataGrid.Column[] {
    const columns: DataGrid.Column[] = [];

    const isSelected = (row: DataGrid.Row) => {
      return props.outputsSelected[row.key];
    };

    // Checkbox
    columns.push({
      name: 'output-select',
      dataKey: 'selected',
      headerText: '',
      resizable: false,
      sortable: false,
      headerFormatter: CheckboxHeader({
        onCheckboxClicked: props.onHeaderCheckboxClicked,
        isCheckboxSelected: props.isHeaderCheckboxChecked,
        isCheckboxDisabled: props.areFutureOutputsIncluded,
      }),
      cellFormatter: CheckboxCell({
        onCheckboxClicked: props.onCheckboxClicked,
        isRowSelected: isSelected,
        checkboxCellTestIdFn: (row: DataGrid.Row) => `flowOutputCheckboxCell-${row.key}`,
        isRowDisabled: () => props.areFutureOutputsIncluded,
      })
    });

    // Output step name
    columns.push({
      name: 'output-steps',
      dataKey: 'flowOutputSteps',
      headerText: Messages.outputStepsColumnName(),
      resizable: false,
      sortable: false,
      headerFormatter: PlainTextHeader(headerStyle),
      cellFormatter: PlainTextCell('name', 'outputStepNameCell', styleOverride),
      growToFit:true,
    });

    // Output name
    columns.push({
      name: 'output-names-col',
      dataKey: 'flowOutputStepsName',
      headerText: Messages.flowOverviewOutputNameColumnName(),
      resizable: false,
      sortable: false,
      headerFormatter: PlainTextHeader(headerStyle),
      cellFormatter: PlainTextCell('datasourceName', 'outputNameCell', styleOverride)
    });

    // Location type
    columns.push({
      name: 'output-types-col',
      dataKey: 'flowOutputStepsType',
      headerText: Messages.flowScheduledTaskLocationType(),
      resizable: false,
      sortable: false,
      headerFormatter: PlainTextHeader(headerStyle),
      cellFormatter: PlainTextCellWithTransform(
        'dataConnection',
        (dataConnection) => dataConnection.connectionTypeDisplayName,
        'outputTypeCell',
        styleOverride
      )
    });

    // Location
    columns.push({
      name: 'output-locations-col',
      dataKey: 'flowOutputStepsLocation',
      headerText: Messages.flowScheduledTaskLocation(),
      resizable: false,
      sortable: false,
      headerFormatter: PlainTextHeader(headerStyle),
      cellFormatter: PlainTextCell('outputLocation', 'outputLocationCell', styleOverride)
    });

    const getIsIncremental = (rowKey: string) => String(props.outputsIsIncremental[rowKey] || false);

    const onSelectChange = (row: DataGrid.Row, isIncremental: string) => {
      props.onIsIncrementalChange(row, isIncremental === String(null) ? null : isIncremental === String(true));
    };

    // Incremental Refresh
    if (FeatureFlags.incrementalRefresh) {
      columns.push({
        name: 'refresh-types-col',
        dataKey: 'flowOutputStepsType',
        headerText: Messages.writeOptionsRefreshType(),
        sortable: false,
        resizable: false,
        growToFit: true,
        headerFormatter: PlainTextHeader(headerStyle),
        cellFormatter:  ListCell(getIsIncremental, onSelectChange, getRefreshMenuItemsForSingleOutput, getDisplayText, props.areFutureOutputsIncluded),
      });
    }
    return columns;
  }

  // These columns previously used the old Data-Grid v1
  export function columns_v1(
    isCheckboxHeaderChecked: boolean,
    onCheckboxHeaderClicked: () => void,
    onCheckboxClicked: (row: DataGrid.Row) => void,
    onIsIncrementalChange: (row: DataGrid.Row, isIncremental: boolean) => void,
    outputsSelected: { readonly [key: string]: boolean },
    outputsIsIncremental: { readonly [key: string]: boolean },
    isIncrementalEnabled: boolean,
    isRunAllIncremental: boolean,
    selectAll: boolean,
  ): DataGrid.Column[] {
    const columns: DataGrid.Column[] = [];

    const isSelected = (rowKey: string) => {
      return outputsSelected[rowKey];
    };

    const isDisabled = selectAll;

    columns.push({
      name: 'output-steps',
      dataKey: 'flowOutputSteps',
      headerText: Messages.outputStepsColumnName(),
      sortable: false,
      headerFormatter: selectAll ?
        SortableHeader.Formatter() :
        DataGridCells.CheckboxHeaderWithText(isCheckboxHeaderChecked, onCheckboxHeaderClicked, isDisabled),
      cellFormatter: selectAll ?
        PlainTextCell('name')
        : DataGridCells.CheckboxCellWithText('name', isSelected, onCheckboxClicked, isDisabled),
      growToFit:true,
      preferredWidth: 300
    });

    columns.push({
      name: 'output-names-col',
      dataKey: 'flowOutputStepsName',
      headerText: Messages.flowOverviewOutputNameColumnName(),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: PlainTextCell('datasourceName')
    });

    columns.push({
      name: 'output-types-col',
      dataKey: 'flowOutputStepsType',
      headerText: Messages.flowScheduledTaskLocation(),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: PlainTextCellWithTransform('dataConnection',
      (dataConnection) => dataConnection.connectionTypeDisplayName)
    });

    const getIsIncremental = (rowKey: string) => {
      if (selectAll)
        return String(isRunAllIncremental ? true : null);
      else
        return String(outputsIsIncremental[rowKey] || false);
    };

    const onSelectChange = (row: DataGrid.Row, isIncremental: string) => {
      onIsIncrementalChange(row, isIncremental === String(null) ? null : isIncremental === String(true));
    };

    let getMenuItems: (row: DataGrid.Row) => ListItem[];

    if (!selectAll)
      getMenuItems = getRefreshMenuItemsForSingleOutput;
    else if (isIncrementalEnabled)
      getMenuItems = getRefreshMenuItemsForRunAll;
    else
      getMenuItems = getRefreshMenuItemsForRunFull;

    if (FeatureFlags.incrementalRefresh) {
      columns.push({
        name: 'refresh-types-col',
        dataKey: 'flowOutputStepsType',
        headerText: Messages.writeOptionsRefreshType(),
        resizable: false,
        sortable: false,
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.ListCell(getIsIncremental, onSelectChange, getMenuItems, getDisplayText, selectAll),
        growToFit: true,
        preferredWidth: 300
      });
    }

    return columns;
  }

  export function futureOutputsRefreshMenuItems(isIncremental: boolean) {
    return isIncremental ? getRefreshMenuItemsForRunAll() : getRefreshMenuItemsForRunFull();
  }

  function getRefreshMenuItemsForRunFull(){
    return [{
      value: String(null),
      content: writeOptionsRunAllFull(false),
    }];
  }

  function getRefreshMenuItemsForRunAll(){
    return [{
      value: String(null),
      content: writeOptionsRunAllFull(false),
    }, {
      value: String(true),
      content: writeOptionsRunAllIncremental(),
    }];
  }

  function getRefreshMenuItemsForSingleOutput(row: DataGrid.Row) {
    const output = row.data as FlowOutputStepInfo;

    if (output.isIncrementalEnabled) {
      return [
        {
          value: String(false),
          content: writeOptionsFull(output, false),
        }, {
          value: String(true),
          content: writeOptionsIncremental(output),
        }];
    }
    else {
      return [
        {
          value: String(false),
          content: writeOptionsFull(output, false),
        }];
    }
  }

  export function getDisplayText(item: ListItem){
    return (item.content as RunFlowSubMenuItem).props.displayText;
  }
}
